import React, { useState, useCallback } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { upsertTenant, updateTenantOnServer } from '../services/tenantService';
import ValidatedEmailInput from '../components/ValidatedEmailInput';
import { formatPhone } from '../utils/phoneFormatting';
import DepartmentAddressField from '../components/shared-forms/DepartmentAddressField';
import type { DepartmentAddress } from '../components/shared-forms/DepartmentAddressField';

// ============================================================
// Types
// ============================================================

interface EditDepartmentModalProps {
  tenant: any;
  onClose: () => void;
  onSaved: (updated: any) => void;
}

// ============================================================
// Helpers
// ============================================================

function Field({ label, children, span2 }: { label: string; children: React.ReactNode; span2?: boolean }) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500';
const selectCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 bg-white';

// ============================================================
// EditDepartmentModal
// ============================================================

export default function EditDepartmentModal({ tenant, onClose, onSaved }: EditDepartmentModalProps) {
  const addr = tenant.address || {};
  const billing = tenant.billing || {};
  const sub = billing.subscription || {};
  const pricing = billing.pricing || {};
  const plan = tenant.inspectionPlan || {};

  const [form, setForm] = useState({
    name: tenant.name || '',
    departmentType: tenant.departmentType || '',
    departmentClassification: tenant.departmentClassification || '',
    timezone: tenant.timezone || '',
    website: tenant.website || '',
    phone: tenant.phone || '',
    contactEmail: tenant.contactEmail || '',
    status: tenant.status || 'active',
    tier: tenant.tier || 'professional',
    // Address
    street: addr.street || '',
    city: addr.city || '',
    state: addr.state || '',
    zip: addr.zip || '',
    county: addr.county || '',
    country: addr.country || '',
    addressValidationStatus: (addr.validationStatus ?? 'needsValidation') as string,
    addressValidationMessage: addr.validationMessage || '',
    addressValidatedAt: addr.validatedAt || '',
    // Billing
    billingCycle: pricing.billingCycle || 'annual',
    contractedAmount: pricing.contractedAmount != null ? String(pricing.contractedAmount) : '',
    renewalDate: sub.renewalDate || '',
    autoRenew: sub.autoRenew ?? true,
    billingContactName: billing.billingContact?.name || '',
    billingContactEmail: billing.billingContact?.email || '',
    // Inspection plan
    volumeBand: plan.volumeBand || '',
    maxSeriesPerYear: plan.maxSeriesPerYear != null ? String(plan.maxSeriesPerYear) : '',
    commitmentTerm: plan.commitmentTerm || '',
    annualPrice: plan.annualPrice != null ? String(plan.annualPrice) : '',
    overagePolicy: plan.overagePolicy || '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'general' | 'address' | 'billing'>('general');

  const set = (key: keyof typeof form, value: string | boolean) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleAddressChange = useCallback((next: DepartmentAddress) => {
    setForm((f) => ({
      ...f,
      street: next.street ?? '',
      city: next.city ?? '',
      state: next.state ?? '',
      zip: next.zip ?? '',
      county: next.county ?? '',
      country: next.country ?? '',
      addressValidationStatus: next.validationStatus ?? 'needsValidation',
      addressValidationMessage: next.validationMessage ?? '',
      addressValidatedAt: next.validatedAt ?? '',
    }));
  }, []);

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Department name is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const updated = {
        ...tenant,
        name: form.name.trim(),
        departmentType: form.departmentType,
        departmentClassification: form.departmentClassification,
        timezone: form.timezone,
        website: form.website,
        phone: form.phone,
        contactEmail: form.contactEmail,
        status: form.status,
        tier: form.tier,
        address: {
          ...addr,
          street: form.street,
          city: form.city,
          state: form.state,
          zip: form.zip,
          county: form.county,
          country: form.country,
          validationStatus: form.addressValidationStatus || undefined,
          validationMessage: form.addressValidationMessage || undefined,
          validatedAt: form.addressValidatedAt || undefined,
        },
        billing: {
          ...billing,
          billingContact: {
            ...(billing.billingContact || {}),
            name: form.billingContactName,
            email: form.billingContactEmail,
          },
          subscription: {
            ...sub,
            renewalDate: form.renewalDate || undefined,
            autoRenew: form.autoRenew,
          },
          pricing: {
            ...pricing,
            billingCycle: form.billingCycle,
            contractedAmount: form.contractedAmount !== '' ? Number(form.contractedAmount) : undefined,
          },
        },
        inspectionPlan: {
          ...plan,
          volumeBand: form.volumeBand,
          maxSeriesPerYear: form.maxSeriesPerYear !== '' ? Number(form.maxSeriesPerYear) : undefined,
          commitmentTerm: form.commitmentTerm,
          annualPrice: form.annualPrice !== '' ? Number(form.annualPrice) : undefined,
          overagePolicy: form.overagePolicy,
        },
      };
      await upsertTenant(updated);
      onSaved(updated);
      updateTenantOnServer(tenant._id, updated).catch((err) => {
        console.warn('[EditDepartmentModal] Failed to sync department update to server:', err);
      });
    } catch (err: any) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const SECTIONS = [
    { id: 'general' as const, label: 'General' },
    { id: 'address' as const, label: 'Address' },
    { id: 'billing' as const, label: 'Billing' },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Edit Department</h3>
            <p className="text-xs text-gray-500 mt-0.5 font-mono">{tenant.slug}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Section tabs */}
        <div className="flex border-b border-gray-200 shrink-0 bg-white">
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className="px-5 py-2.5 text-sm font-medium border-b-2 transition-colors"
              style={{
                borderBottomColor: activeSection === s.id ? '#ea580c' : 'transparent',
                color: activeSection === s.id ? '#ea580c' : '#6b7280',
              }}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="flex items-center gap-2 px-4 py-2.5 mb-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* ── General ── */}
          {activeSection === 'general' && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Department Name *" span2>
                <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} className={inputCls} placeholder="Saguaro Valley Fire Department" />
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={(e) => set('status', e.target.value)} className={selectCls}>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="demo">Demo</option>
                  <option value="expired">Expired</option>
                </select>
              </Field>
              <Field label="Tier">
                <select value={form.tier} onChange={(e) => set('tier', e.target.value)} className={selectCls}>
                  <option value="professional">Professional</option>
                  <option value="advanced">Advanced</option>
                  <option value="premium">Premium</option>
                </select>
              </Field>
              <Field label="Department Type">
                <input type="text" value={form.departmentType} onChange={(e) => set('departmentType', e.target.value)} className={inputCls} placeholder="Municipal" />
              </Field>
              <Field label="Classification">
                <input type="text" value={form.departmentClassification} onChange={(e) => set('departmentClassification', e.target.value)} className={inputCls} placeholder="Career" />
              </Field>
              <Field label="Timezone">
                <input type="text" value={form.timezone} onChange={(e) => set('timezone', e.target.value)} className={inputCls} placeholder="America/Phoenix" />
              </Field>
              <Field label="Website">
                <input type="text" value={form.website} onChange={(e) => set('website', e.target.value)} className={inputCls} placeholder="https://example.gov" />
              </Field>
              <Field label="Phone">
                <input type="tel" value={form.phone} onChange={(e) => set('phone', formatPhone(e.target.value))} className={inputCls} placeholder="(555) 555-0100" />
              </Field>
              <Field label="Contact Email">
                <ValidatedEmailInput value={form.contactEmail} onChange={(v) => set('contactEmail', v)} className={inputCls + ' pr-9'} placeholder="admin@dept.gov" />
              </Field>
            </div>
          )}

          {/* ── Address ── */}
          {activeSection === 'address' && (
            <DepartmentAddressField
              value={{
                street: form.street,
                city: form.city,
                state: form.state,
                zip: form.zip,
                county: form.county,
                country: form.country,
                validationStatus: form.addressValidationStatus as DepartmentAddress['validationStatus'],
                validationMessage: form.addressValidationMessage || undefined,
                validatedAt: form.addressValidatedAt || undefined,
              }}
              onChange={handleAddressChange}
            />
          )}

          {/* ── Billing ── */}
          {activeSection === 'billing' && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Billing Cycle">
                <select value={form.billingCycle} onChange={(e) => set('billingCycle', e.target.value)} className={selectCls}>
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                </select>
              </Field>
              <Field label="Contracted Amount ($)">
                <input type="number" min="0" value={form.contractedAmount} onChange={(e) => set('contractedAmount', e.target.value)} className={inputCls} placeholder="0" />
              </Field>
              <Field label="Renewal Date">
                <input type="date" value={form.renewalDate ? form.renewalDate.slice(0, 10) : ''} onChange={(e) => set('renewalDate', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Auto-Renew">
                <select value={form.autoRenew ? 'true' : 'false'} onChange={(e) => set('autoRenew', e.target.value === 'true')} className={selectCls}>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </Field>
              <Field label="Billing Contact Name">
                <input type="text" value={form.billingContactName} onChange={(e) => set('billingContactName', e.target.value)} className={inputCls} placeholder="Jane Smith" />
              </Field>
              <Field label="Billing Contact Email">
                <ValidatedEmailInput value={form.billingContactEmail} onChange={(v) => set('billingContactEmail', v)} className={inputCls + ' pr-9'} placeholder="billing@dept.gov" />
              </Field>

              <div className="col-span-2 border-t border-gray-200 pt-4 mt-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Inspection Plan</p>
              </div>
              <Field label="Volume Band">
                <input type="text" value={form.volumeBand} onChange={(e) => set('volumeBand', e.target.value)} className={inputCls} placeholder="small" />
              </Field>
              <Field label="Max Series / Year">
                <input type="number" min="0" value={form.maxSeriesPerYear} onChange={(e) => set('maxSeriesPerYear', e.target.value)} className={inputCls} placeholder="500" />
              </Field>
              <Field label="Commitment Term">
                <select value={form.commitmentTerm} onChange={(e) => set('commitmentTerm', e.target.value)} className={selectCls}>
                  <option value="">— None —</option>
                  <option value="month-to-month">Month-to-Month</option>
                  <option value="1-year">1 Year</option>
                  <option value="2-year">2 Years</option>
                  <option value="3-year">3 Years</option>
                </select>
              </Field>
              <Field label="Annual Price ($)">
                <input type="number" min="0" value={form.annualPrice} onChange={(e) => set('annualPrice', e.target.value)} className={inputCls} placeholder="0" />
              </Field>
              <Field label="Overage Policy">
                <select value={form.overagePolicy} onChange={(e) => set('overagePolicy', e.target.value)} className={selectCls}>
                  <option value="">— None —</option>
                  <option value="block">Block</option>
                  <option value="charge">Charge</option>
                  <option value="notify">Notify</option>
                </select>
              </Field>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 shrink-0">
          <div className="text-xs text-gray-400">
            {SECTIONS.indexOf(SECTIONS.find((s) => s.id === activeSection)!) + 1} of {SECTIONS.length} sections
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              className="px-5 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
